import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = guest, object = user
  const [checked, setChecked] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Returns either { user } or { mfaRequired: true, mfaToken } for two-step
  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.mfa_required) {
      return { mfaRequired: true, mfaToken: data.mfa_token, rememberAllowed: !!data.remember_device_allowed };
    }
    setUser(data);
    return { user: data };
  }, []);

  const verifyMfa = useCallback(async (mfaToken, code, rememberDevice = false) => {
    const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfaToken, code, remember_device: rememberDevice });
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { void e; }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, checked, login, verifyMfa, register, logout, refresh }),
    [user, checked, login, verifyMfa, register, logout, refresh],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
